<template>
  <div>

    <div class="fixed-main-box">
      <!-- Schedule Form -->
      <b-row class="mb-3" v-if="!viewOnly">
        <b-col cols="12" class="pb-2">
          <h4><strong>Schedule Message</strong></h4>
        </b-col>
        <b-col v-if="!sendAtText.error" cols="12" class="d-flex justify-content-start align-items-center">
          <div>
            This message will be sent at
          </div>
          <div style="width: 100px;" class="ml-2">
            <b-form-select v-model="relativeSendAtForm.atHour" :options="hourOptions" />
          </div>
          <div class="ml-2">:</div>
          <div style="width: 100px;" class="ml-2">
            <b-form-select v-model="relativeSendAtForm.atMinute" :options="minuteOptions" />
          </div>
          <div class="ml-2" v-html="sendAtText.text">
          </div>
        </b-col>
        <b-col v-else cols="12">
          <div v-html="sendAtText.text"></div>
        </b-col>
      </b-row>

      <!-- Main Form -->
      <b-row>
        <b-col v-if="isScheduled && !viewOnly" cols="12">
          <b-alert show>You cannot edit this message, as it is either delivered or in progress.</b-alert>
        </b-col>
        <b-col cols="12">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="formFields"
            v-bind="form"
            v-model="form"
            :disabled="viewOnly"
          />
        </b-col>
      </b-row>

      <!-- Input Form with preview -->
      <b-row>
        <b-col cols="12">
          <h3 class="pt-3">
            <strong>Message Content</strong>
          </h3>
        </b-col>
        <b-col cols="12" lg="7">
          <div class="mb-3">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview. Note that the test message will be sent to the mobile app of the logged-in admin user only.</div>
          <div class="mb-3">Android devices support up to 240 characters. iPhone devices support up to 178 characters. Excess characters will be truncated as appropriate for the platform.</div>
          <push-preview
            class="mb-3"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
        </b-col>
        <b-col cols="12" lg="5">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="inputFormFields"
            v-bind="inputForm"
            v-model="inputForm"
            :disabled="viewOnly"
          />
        </b-col>
      </b-row>

      <!-- Variables that can be used -->
      <b-row>
        <b-col>
          <b-card no-body class="mb-1">
            <b-card-header class="d-flex justify-content-between align-items-center bg-white">
              <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
                <strong>Template variables you can use</strong>
              </div>
              <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
                <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
                <font-awesome-icon v-else icon="chevron-down" size="sm"/>
              </b-button>
            </b-card-header>
            <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
              <b>User related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Job related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Leaderboard related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            </b-collapse>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Buttons -->
    <div v-if="!viewOnly" class="fixed-buttons-box border-top d-flex justify-content-between align-items-center">
      <!-- Delete Button -->
      <b-link
        v-if="!isScheduled"
        class="text-danger"
        @click="$bvModal.show('delete-push-message')"
      >Delete</b-link>

      <!-- Save Button -->
      <b-button
        variant="primary"
        @click="input('pushMessage')"
        class="float-right"
        :disabled="isScheduled || isSceduledInPast"
        pill
      >
        Save Message
      </b-button>
    </div>

    <!-- Test Messsage Form -->
    <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
      <b-row>
        <b-col v-if="!viewOnly">
          <b-form>
            <test-message-form
              :body="body"
              :job="inputForm.job"
              :jobs="inputForm.jobs || null"
              :channelID="form.channel"
              :contentShare="inputForm.contentShare"
              :contentShares="inputForm.contentShares || null"
              :collectiveGoal="collectiveGoal"
              :isIndividual="isIndividual"
              :campaignID="campaignID"
              :campaignComponentID="campaignComponentID"
            />
          </b-form>
        </b-col>
        <b-col v-else>
          <b-alert variant="warning" show>You cannot send a test message in view only mode.</b-alert>
        </b-col>
      </b-row>
    </b-modal>

    <!-- DELETE MODAL -->
    <b-modal
      v-if="!isScheduled"
      id="delete-push-message"
      title="Delete Push Message"
      ok-title="Delete"
      cancel-title="Cancel"
      @ok="$emit('delete', { moveComponents })"
      cancel-variant="light"
      ok-variant="danger"
    >
      <p>Are you sure you want to delete this Push message?</p>
      <b-form-checkbox v-model="moveComponents" name="check-button" switch>
        Move all the following messages forward
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import pushTemplates from '@src/views/campaign-admin/templates/push';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import PushPreview from '@src/components/campaign-admin/message-previews/push/generic';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import TurndownService from 'turndown';
import updateMessageMixin from '@/mixins/updateMessage';

export default {
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    PushPreview,
  },
  data() {
    const { pushMessage } = this.value || {};
    delete pushMessage.__typename;
    const { template, inputs, body, job, contentShare, jobs, contentShares, relativeSendAt, ...rest } = pushMessage || {};
    return {
      moveComponents: false,
      form: {
        to: 'ALL_USERS',
        sendAt: '',
        status: 'DRAFT',
        ...(rest || {}),
      },
      relativeSendAt: {
        ...(relativeSendAt || {})
      },
      relativeSendAtForm: {
        atHour: relativeSendAt.atHour || 12,
        atMinute: relativeSendAt.atMinute || 0,
      },
      isScheduled: false,
      templateID: template || null,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          acc[key] = inputs[key];
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {}),
        ...(jobs ? { jobs } : {}),
        ...(contentShares ? { contentShares } : {})
      },
      displayVariables: false,
    };
  },
  mixins: [
    gridMixin,
    updateMessageMixin
  ],
  computed: {
    template() {
      const { templateID } = this;
      return pushTemplates.find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual, template } = this;
      const { canSendToChannel } = template;
      return {
        ALL_USERS: `All Participants: Everyone in the campaign - ${isIndividual ? 'sent as a direct push notification' : 'posted in a group'}`,
        REGISTERED_USERS: `Active Participants: Those who have engaged with the campaign - ${isIndividual ? 'sent as a direct push notification' : 'posted in a group'}`,
        INVITED_USERS: `Inactive Participants: Those who have not engaged with the campaign yet - ${isIndividual ? 'sent as a direct push notification' : 'posted in a group'}`,
        ...(canSendToChannel ? { CHANNEL: 'Channel: Sent as a channel message' } : {}),
      };
    },
    formFields() {
      const { campaignID } = this;
      console.log({ campaignID });
      // const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        ...{
          to: {
            label: 'To',
            type: 'SELECT',
            required: true,
            choices: this.toChoices,
            disabled: this.isScheduled,
            helpText: 'The target group of this push notification'
          },
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          helpText: 'The status of this push notification. The message will not be delivered until the status is set to "Ready"',
          disabled: this.isScheduled,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template, jobsWhere, contentSharesWhere } = this;
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                id: 'NONE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the push notification',
          },
        } : {}),
        ...(this.areJobs ? {
          jobs: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                id: 'NONE',
              },
              many: true,
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 jobs you want to include in the push notification',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: contentSharesWhere || {
                id: 'NONE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the push notification',
          },
        } : {}),
        ...(this.arePiecesOfContent ? {
          contentShares: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: true,
              where: contentSharesWhere || {
                id: 'NONE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 pieces of content you want to include in the push notification',
          },
        } : {}),
        ...inputFields,
      };
    },
    body() {
      const { template, inputForm } = this;
      const turndownService = new TurndownService({ strongDelimiter: '*' });
      if (!template) return null;
      let { body } = template;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        Object.keys(inputForm).forEach(key => {
          if (typeof inputForm[key] === 'string') {
            replacedBody = replacedBody.replaceAll(`[[${key}]]`, turndownService
              .turndown(inputForm[key])
              .replaceAll('\\', '')
              .replaceAll('\n', '\\n')
            );
          }
        });
        return replacedBody;
      };
      const { jobs, contentShares } = inputForm;
      if (body && Array.isArray(body.blocks) && body.blocks.find(({ type }) => type === 'blocks') && template.blocks &&
        (Array.isArray(jobs) || Array.isArray(contentShares))) {
        const block = body.blocks.find(({ type }) => type === 'blocks');
        const blockIndex = body.blocks.findIndex(({ type }) => type === 'blocks');
        const { key: blockKey } = block;
        let items = [];
        if (blockKey === 'BLOCK_JOB') {
          const { jobs } = inputForm;
          items = jobs;
        }
        if (blockKey === 'BLOCK_CONTENT') {
          const { contentShares } = inputForm;
          items = contentShares;
        }
        console.log({ items });
        const blocks = items.map(({ id }) => JSON.parse(JSON.stringify(template.blocks[blockKey]).replaceAll('[ID]', id)));
        console.log({ blocks });

        // replace block in the body.blocks array with the new blocks
        body = {
          ...body,
          blocks: [
            ...body.blocks.slice(0, blockIndex),
            ...(blocks.reduce((r, block) => {
              if (Array.isArray(block)) r.push(...block);
              else r.push(block);
              return r;
            }, [])),
            ...body.blocks.slice(blockIndex + 1),
          ],
        };
        console.log({ body });
      }
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], tunrDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        if (typeof inputForm[key] === 'string') {
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, turndownService
            .turndown(inputForm[key])
            .replaceAll('\\', '')
            .replaceAll('\n', '\\n')
          );
        }
      });
      console.log({ replacedBody: JSON.parse(replacedBody) });
      return replacedBody;
    },
    isIndividual() {
      // const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      // const { relations } = this.template || {};
      // return !!keys.filter(key => this.usedVariables.includes(key)).length || (Array.isArray(relations) && (relations.includes('MANY_JOBS') || relations.includes('MANY_CONTENT')));
      return true;
    },
  }
};
</script>

<style scoped>
.fixed-buttons-box {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 8px 10px 8px 10px;
  border-top: 1px solid #e9ecef;
  z-index: 1000;
}
.fixed-main-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 60px;
  padding: 20px;
  overflow-y: scroll;
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-push-message-relative-dates.vue